import { useMemo, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import {
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridSortDirection,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { MAX_PINNED_COLUMNS } from '@common/types/constants';
import { buildGridColumns, getReorderedColumns } from '@util/dataGrid';
import {
  useReportFoldersTableDataGridStore,
  PersistedReportFolderColumn,
  initialReportFolderColumns,
} from '@store';
import { TruncatedTextWithConditionalTooltip } from '@components';
import { theme } from '@store';
import { formatDate } from '@util/date';
import { Star, Https } from '@mui/icons-material';
import { Dropdown, DropdownMenuItem } from '@components';
import { t } from 'i18next';
import { useOwnProfileQuery } from '@common/cache';
import { ReportFoldersTableEntry } from './useReportFoldersTable';
import { ReportFoldersTableFilters } from './useReportFoldersTable';
import { EditReportFolderModal } from '@views/pages/Reporting/components';
import { ColumnDensity } from '@common/types';

interface IBuildReportFolderColumnsParams {
  handleOpenDeleteModal: (id: string) => void;
  handleMakeReportFolderFavorite: (id: string, isFavorite: boolean) => void;
  makeReportFolderFavoriteIsPending: boolean;
  reportIdToFavorite: string;
  user_id?: string;
  handleOpenEditModal: (id: string) => void;
  density?: ColumnDensity;
}

const buildReportFolderColumns = ({
  handleOpenDeleteModal,
  handleMakeReportFolderFavorite,
  makeReportFolderFavoriteIsPending,
  reportIdToFavorite,
  user_id,
  handleOpenEditModal,
  density,
}: IBuildReportFolderColumnsParams): GridColDef<ReportFoldersTableEntry>[] => [
  {
    field: 'favorite',
    headerName: '',
    width: 60,
    minWidth: 60,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: params => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <IconButton
          onClick={e => {
            e.stopPropagation();
            handleMakeReportFolderFavorite(params.row.id, params.row.favorite);
          }}
          loading={
            makeReportFolderFavoriteIsPending &&
            reportIdToFavorite === params.row.id
          }
          sx={{ height: '24px', width: '24px' }}
        >
          <Star
            color={params.row.favorite ? 'primary' : 'disabled'}
            sx={{
              display:
                makeReportFolderFavoriteIsPending &&
                reportIdToFavorite === params.row.id
                  ? 'none'
                  : 'block',
            }}
          />
        </IconButton>
      </Box>
    ),
  },
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 200,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        width='100%'
        variant='body2'
        text={params.row.name}
      />
    ),
  },
  {
    field: 'created_at',
    headerName: 'Created at',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TruncatedTextWithConditionalTooltip
          text={params.row.created_by?.name}
          variant='body2'
        />
        {density !== 'dense' && (
          <TruncatedTextWithConditionalTooltip
            color='textSecondary'
            variant='caption'
            text={formatDate(params.row.created_at)}
          />
        )}
      </Box>
    ),
  },
  {
    field: 'organisation',
    headerName: 'Organization',
    minWidth: 160,
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        width='100%'
        variant='body2'
        text={params.row.organization?.name}
      />
    ),
  },
  {
    field: 'updated_at',
    headerName: 'Updated at',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        variant='body2'
        sx={{ color: theme.palette.semiBlack.main }}
        text={formatDate(params.row.updated_at)}
      />
    ),
  },
  {
    field: 'report_count',
    headerName: 'Reports',
    minWidth: 120,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        variant='body2'
        text={params.row.report_count}
      />
    ),
  },
  {
    field: 'private',
    headerName: '',
    width: 60,
    minWidth: 60,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: params =>
      params.row.private ? (
        <Https color='disabled' sx={{ height: '24px' }} />
      ) : null,
  },
  {
    field: 'action',
    headerName: '',
    width: 60,
    minWidth: 60,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: params => {
      const isOwner = user_id === params.row.created_by?.id;
      const dropdownMenu: DropdownMenuItem[] = [
        {
          iconName: 'none',
          label: t('reporting.folders.table.edit'),
          onClick: () => handleOpenEditModal(params.row.id),
        },
        {
          iconName: 'none',
          label: t('reporting.folders.table.delete'),
          onClick: () => handleOpenDeleteModal(params.row.id),
          borderTop: true,
        },
      ];
      return (
        <Tooltip
          title={!isOwner ? t('reporting.folders.table.noEdit') : ''}
          arrow
          placement='top'
        >
          <Box>
            <Dropdown menuList={dropdownMenu} actionMenu disabled={!isOwner} />
          </Box>
        </Tooltip>
      );
    },
  },
];

interface IUseReportFoldersDataGridTableProps {
  filters: ReportFoldersTableFilters;
  setFilters: (filters: ReportFoldersTableFilters) => void;
  handleOpenDeleteModal: (id: string) => void;
  handleMakeReportFolderFavorite: (id: string, isFavorite: boolean) => void;
  makeReportFolderFavoriteIsPending: boolean;
  reportIdToFavorite: string;
}

export const useReportFoldersDataGridTable = ({
  filters,
  setFilters,
  handleOpenDeleteModal,
  handleMakeReportFolderFavorite,
  makeReportFolderFavoriteIsPending,
  reportIdToFavorite,
}: IUseReportFoldersDataGridTableProps) => {
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [editFolderId, setEditFolderId] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const apiRef = useGridApiRef();

  const { data: userProfile } = useOwnProfileQuery();

  const {
    columns: persistedColumns,
    setColumns: setPersistedColumns,
    pinnedColumns,
    setPinnedColumns,
    hiddenColumns,
    setHiddenColumns,
    density,
    setDensity,
    resetView,
  } = useReportFoldersTableDataGridStore();

  const { sort_by, sort_order } = filters;

  const handleOpenEditModal = (id: string) => {
    setEditFolderId(id);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditFolderId('');
    setIsEditModalOpen(false);
  };

  const runtimeColumns = useMemo(
    () =>
      buildReportFolderColumns({
        handleOpenDeleteModal,
        handleMakeReportFolderFavorite,
        makeReportFolderFavoriteIsPending,
        reportIdToFavorite,
        user_id: userProfile?.id,
        handleOpenEditModal,
        density,
      }),
    [
      makeReportFolderFavoriteIsPending,
      reportIdToFavorite,
      userProfile?.id,
      density,
    ],
  );

  const columns = buildGridColumns<
    ReportFoldersTableEntry,
    PersistedReportFolderColumn
  >(runtimeColumns, persistedColumns);

  const toPersistedColumn = (
    col: GridColDef<ReportFoldersTableEntry>,
  ): PersistedReportFolderColumn => ({
    field: col.field,
    headerName: col.headerName,
    width: col.width,
    minWidth: col.minWidth,
    flex: col.flex,
    sortable: col.sortable,
    filterable: col.filterable,
    disableColumnMenu: col.disableColumnMenu,
    align: col.align,
    headerAlign: col.headerAlign,
  });

  const handleChangeOrder = (field: string, sort: GridSortDirection) => {
    if (!field || !sort) {
      setFilters({ ...filters, sort_by: 'name', sort_order: 'ASC' });
      return;
    }
    setFilters({
      ...filters,
      sort_by: field as ReportFoldersTableFilters['sort_by'],
      sort_order: sort.toUpperCase() as 'ASC' | 'DESC',
    });
  };

  const onPageModelChange = (model: GridPaginationModel) => {
    setFilters({ ...filters, limit: model.pageSize, page: model.page + 1 });
  };

  const handleColumnOrderChange = (_params: GridColumnOrderChangeParams) => {
    const next = getReorderedColumns(apiRef, columns);
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    const next = columns.map(col =>
      col.field === params.colDef.field
        ? { ...col, width: params.width, flex: undefined }
        : col,
    );
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handlePinnedColumnsChange = (model: GridPinnedColumnFields) => {
    setPinnedColumns({
      left: (model.left ?? []).slice(0, MAX_PINNED_COLUMNS),
      right: (model.right ?? []).slice(0, MAX_PINNED_COLUMNS),
    });
  };

  const columnVisibilityModel: Record<string, boolean> = Object.fromEntries(
    columns.map(col => [col.field, !hiddenColumns.includes(col.field)]),
  );

  const handleColumnVisibilityModelChange = (
    model: Record<string, boolean>,
  ) => {
    setHiddenColumns(
      columns.map(col => col.field).filter(field => model[field] === false),
    );
  };

  const handleResetAll = () => {
    resetView();
  };

  const EditModal = (
    <EditReportFolderModal
      isOpen={isEditModalOpen}
      handleClose={handleCloseEditModal}
      report_folder_id={editFolderId}
    />
  );

  return {
    filters,
    sort_by,
    sort_order,
    handleChangeOrder,
    onPageModelChange,
    columns,
    pinnedColumns,
    handlePinnedColumnsChange,
    columnVisibilityModel,
    handleColumnVisibilityModelChange,
    handleColumnOrderChange,
    handleColumnWidthChange,
    density,
    handleDensityChange: setDensity,
    isResetModalOpen,
    setIsResetModalOpen,
    handleResetAll,
    initialSortFilters: { sort_by: 'name', sort_order: 'ASC' },
    initialColumns: initialReportFolderColumns,
    EditModal,
    apiRef,
  };
};
